import { useState } from "react";
import { useTaxonomy, type ResearchAxis } from "@/hooks/useResearch";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Trash2, Link2, Unlink, Network } from "lucide-react";

const AXES: ResearchAxis[] = ["asset_class", "geography", "sector", "theme", "instrument_type", "custom"];

type KgNode = { id: string; label: string; node_type: string };
type TaxValue = { id: string; axis: ResearchAxis; value: string; kg_node_id?: string | null };

export default function ResearchTaxonomy() {
  const { data: taxonomy } = useTaxonomy();
  const qc = useQueryClient();
  const [axis, setAxis] = useState<ResearchAxis>("asset_class");
  const [value, setValue] = useState("");

  const add = async () => {
    if (!value.trim()) return;
    const { error } = await supabase
      .from("research_taxonomy_values")
      .insert({ axis, value: value.trim() });
    if (error) return toast.error(error.message);
    setValue("");
    qc.invalidateQueries({ queryKey: ["research_taxonomy"] });
    toast.success("Added");
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("research_taxonomy_values").delete().eq("id", id);
    if (error) return toast.error(error.message);
    qc.invalidateQueries({ queryKey: ["research_taxonomy"] });
  };

  const linkKg = async (id: string, kgNodeId: string | null) => {
    const { error } = await supabase
      .from("research_taxonomy_values")
      .update({ kg_node_id: kgNodeId } as any)
      .eq("id", id);
    if (error) return toast.error(error.message);
    qc.invalidateQueries({ queryKey: ["research_taxonomy"] });
    toast.success(kgNodeId ? "Linked to KG node" : "Unlinked");
  };

  const grouped = AXES.map((a) => ({
    axis: a,
    items: ((taxonomy as TaxValue[] | undefined) ?? []).filter((t) => t.axis === a),
  }));

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4 space-y-2">
          <div className="flex gap-2 items-end">
            <div>
              <label className="text-xs text-muted-foreground">Axis</label>
              <Select value={axis} onValueChange={(v) => setAxis(v as any)}>
                <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                <SelectContent>{AXES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <label className="text-xs text-muted-foreground">Value</label>
              <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder="e.g. Emerging Markets" />
            </div>
            <Button onClick={add}>Add</Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Taxonomy values are human-curated filter tags for research items. Link a tag to a Knowledge Graph entity (e.g. "Semiconductors" sector → KG node) so filtering can resolve across both systems.
          </p>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        {grouped.map((g) => (
          <Card key={g.axis}>
            <CardContent className="p-4">
              <h3 className="font-semibold text-sm mb-2">{g.axis}</h3>
              <div className="flex flex-wrap gap-1">
                {g.items.length === 0 && <p className="text-xs text-muted-foreground">No entries</p>}
                {g.items.map((t) => (
                  <Badge key={t.id} variant="outline" className="gap-1 pl-2">
                    {t.value}
                    {t.kg_node_id && (
                      <Network className="h-3 w-3 text-primary" aria-label="Linked to KG" />
                    )}
                    <KgLinkPopover
                      currentKgId={t.kg_node_id ?? null}
                      onPick={(kgId) => linkKg(t.id, kgId)}
                    />
                    <button onClick={() => remove(t.id)} className="opacity-50 hover:opacity-100">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function KgLinkPopover({
  currentKgId,
  onPick,
}: {
  currentKgId: string | null;
  onPick: (kgId: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const { data: nodes, isLoading } = useQuery({
    queryKey: ["kg_node_search", q],
    enabled: open,
    queryFn: async () => {
      let query = (supabase as any)
        .from("research_kg_nodes")
        .select("id,label,node_type")
        .is("merged_into_id", null)
        .order("last_seen_at", { ascending: false })
        .limit(25);
      if (q.trim()) query = query.ilike("label", `%${q.trim()}%`);
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as KgNode[];
    },
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="opacity-50 hover:opacity-100"
          title={currentKgId ? "Change KG link" : "Link to KG node"}
        >
          <Link2 className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="start">
        <Input
          autoFocus
          placeholder="Search KG nodes..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-8 text-xs mb-2"
        />
        <div className="max-h-64 overflow-y-auto space-y-1">
          {currentKgId && (
            <button
              onClick={() => { onPick(null); setOpen(false); }}
              className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-muted flex items-center gap-2 text-destructive"
            >
              <Unlink className="h-3 w-3" /> Unlink
            </button>
          )}
          {isLoading && <p className="text-xs text-muted-foreground px-2 py-1">Loading...</p>}
          {nodes && nodes.length === 0 && (
            <p className="text-xs text-muted-foreground px-2 py-1">No matches</p>
          )}
          {nodes?.map((n) => (
            <button
              key={n.id}
              onClick={() => { onPick(n.id); setOpen(false); }}
              className={`w-full text-left text-xs px-2 py-1.5 rounded hover:bg-muted ${
                n.id === currentKgId ? "bg-muted" : ""
              }`}
            >
              <div className="font-medium truncate">{n.label}</div>
              <div className="text-[10px] text-muted-foreground">{n.node_type}</div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
